import React, { useEffect, useRef, useState } from 'react';

import './Marquee.scss';

interface MarqueeProps {
  text: string;
  link?: string;
  space?: number;
  speed?: number;
  height?: number;
  visibleUntil?: number; // Unix timestamp in milliseconds
}

const Marquee: React.FC<MarqueeProps> = ({ text, link, space = 50, speed = 100, height = 30, visibleUntil }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRef = useRef<HTMLSpanElement>(null);
  const [itemCount, setItemCount] = useState(8);

  const ANIMATION_TRANSLATE_PERCENT = 25;
  const totalSets = Math.ceil(100 / ANIMATION_TRANSLATE_PERCENT);

  // Check if marquee should be visible
  const isVisible = visibleUntil ? Date.now() < visibleUntil : true;

  useEffect(() => {
    // Set the CSS variable for marquee height
    if (isVisible) {
      document.documentElement.style.setProperty('--marquee-height', `${height}px`);
    } else {
      document.documentElement.style.setProperty('--marquee-height', '0px');
    }

    return () => {
      // Clean up the CSS variable on unmount
      document.documentElement.style.removeProperty('--marquee-height');
    };
  }, [height, isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    const calculateItemCount = () => {
      if (containerRef.current && itemRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const itemWidth = itemRef.current.offsetWidth;

        if (itemWidth > 0) {
          const itemsToFillScreen = Math.ceil(containerWidth / itemWidth) + 2;
          setItemCount(itemsToFillScreen * totalSets);
        }
      }
    };

    calculateItemCount();
    window.addEventListener('resize', calculateItemCount);

    return () => window.removeEventListener('resize', calculateItemCount);
  }, [text, space, totalSets, isVisible]);

  const handleClick = () => {
    if (link) {
      window.open(link, '_blank', 'noopener,noreferrer');
    }
  };

  // Don't render if past the visible timestamp
  if (!isVisible) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className={`tw-relative tw-z-[1000] tw-flex tw-w-full tw-items-center tw-overflow-hidden tw-whitespace-nowrap tw-bg-[#FF0000] ${
        link ? 'tw-cursor-pointer hover:tw-opacity-90' : ''
      }`}
      style={{ height: `${height}px` }}
      onClick={handleClick}
    >
      <div className="marquee-animation tw-flex" style={{ animationDuration: `${speed}s` }}>
        {Array.from({ length: itemCount }).map((_, index) => (
          <span
            key={index}
            ref={index === 0 ? itemRef : null}
            className="tw-whitespace-nowrap tw-font-['Rajdhani'] tw-text-[18px] tw-font-bold tw-text-white"
            style={{ paddingLeft: `${space}px`, paddingRight: `${space}px`, lineHeight: `${height}px` }}
          >
            {text}
          </span>
        ))}
      </div>
    </div>
  );
};

export default Marquee;
